import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { apiErrorMessage } from 'src/api';
import { orderingCalc } from 'src/api/cart';
import { fetchMyOffers, fetchOffers, submitAndPayOffer, submitOffer } from 'src/api/offer';
import { LIST_VIEW_QUERY_PARAM } from 'src/components/account/organization/dashboard/types';
import { ROUTE_ACCOUNT_ORGANIZATION_OVERVIEW } from 'src/config/account-routes';
import i18next from 'src/config/i18next';
import { CartOfferRequestDTO } from 'src/entities/cart/request/CartOfferRequestDTO';
import CartRequestDTO from 'src/entities/cart/request/CartRequestDTO';
import ShoppingCart from 'src/entities/cart/response/ShoppingCart';
import {
    failureOrderingCalc,
    requestOrderingCalc,
    resetShoppingCart,
    successOrderingCalc,
} from 'src/store/cart/actions';
import { calcCurrentCart, leaveCart } from 'src/store/cart/calcEpoch';
import { getShoppingCart, ShoppingCartEntityState } from 'src/store/cart/selectors';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import { flushPersistedState } from 'src/store/persistor';
import { ROUTE_CART, ROUTE_CHECKOUT, ROUTE_DIRECTSELL_CONFIRMATION } from '../../config/routes';
import {
    APPLY_OFFER,
    clearParkedCart,
    CLEAR_MY_OFFERS,
    EDIT_OFFER,
    FETCH_MY_OFFERS,
    FETCH_OFFERS,
    parkCart,
    requestMyOffers,
    requestMyOffersFailure,
    requestMyOffersSuccess,
    requestOffers,
    requestOffersFailure,
    requestOffersSuccess,
    RESTORE_PARKED_CART,
    SUBMIT_AND_PAY_OFFER,
    SUBMIT_OFFER,
    UPDATE_OFFER,
} from './action';
import { ParkedCartState } from './reducer';
import { getParkedCart, holdsAppliedOffer } from './selectors';

/**
 * Edit Offer
 */
function* editOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const cartOfferRequestDTO = CartOfferRequestDTO.fromOffer(action.offer);

    const result = yield call(calcCurrentCart, cartOfferRequestDTO);
    if (!result) return;
    const { error, response } = result;

    if (error) yield put(failureOrderingCalc(error));
    else {
        yield put(successOrderingCalc(response, false));
        window.location.replace(ROUTE_CART.path);
    }
}

export function* generateEditOfferWatcher() {
    yield takeLatest(EDIT_OFFER, editOfferSaga);
}

/**
 * Update Offer
 */
function* updateOffer(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const result = yield call(calcCurrentCart, cartOfferRequestDTO);
    if (!result) return;
    const { error, response } = result;

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, existingShoppingCart.content.containsOrderDirectlyProduct));
}

export const updateOfferSagaForTest = updateOffer;

export function* generateUpdateOfferWatcher() {
    yield takeLatest(UPDATE_OFFER, updateOffer);
}

/**
 * R70 item 12: the saved offer empties the cart before the page is left — locally (no calc), with the answers of
 * calcs still on their way dropped and the empty cart written to sessionStorage before the navigation.
 */
function* leaveCartFor(target: string): SagaIterator {
    leaveCart();
    yield put(resetShoppingCart());
    yield call(flushPersistedState);
    window.location.replace(target);
}

/**
 * Submit Offer
 */
function* submitOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const { error } = yield call(submitOffer, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield call(leaveCartFor, `${ROUTE_ACCOUNT_ORGANIZATION_OVERVIEW.path}?${LIST_VIEW_QUERY_PARAM}=offers`);
}

export const submitOfferSagaForTest = submitOfferSaga;

export function* generateSubmitOfferWatcher() {
    yield takeLatest(SUBMIT_OFFER, submitOfferSaga);
}

/**
 * Submit Offer
 */
function* submitAndPayOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const { error, response } = yield call(submitAndPayOffer, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        const { paymentLink, orderId } = response.data;
        yield call(
            leaveCartFor,
            ROUTE_DIRECTSELL_CONFIRMATION.interpolatedClone(undefined, { paymentLink, orderId }).path
        );
    }
}

export function* generateSubmitAndPayOfferWatcher() {
    yield takeLatest(SUBMIT_AND_PAY_OFFER, submitAndPayOfferSaga);
}

/**
 * Apply Offer
 */
function* applyOfferSaga(action: AnyAction): SagaIterator {
    const current: ShoppingCartEntityState = yield select(getShoppingCart);
    const currentContent = yield select((state: any) => state.cart?.content);
    const parked: ParkedCartState = yield select(getParkedCart);
    const offerInCart: boolean = yield select(holdsAppliedOffer);

    yield put(requestOrderingCalc());

    const cartOfferRequestDTO = CartRequestDTO.fromExistingOffer(action.offer);

    const result = yield call(calcCurrentCart, cartOfferRequestDTO);
    if (!result) return;
    const { error, response } = result;

    if (error) yield put(failureOrderingCalc(error));
    else {
        // R65 item 1: cart and offer are independent — the shop goods wait until the offer leaves the cart
        const shopGoods = current.content.isEmpty() || current.content.isOffer ? undefined : currentContent;
        yield put(parkCart(action.offer.publicId, offerInCart ? parked.cart : shopGoods));
        yield put(successOrderingCalc(response, true));
        window.location.replace(ROUTE_CHECKOUT.path);
    }
}

export function* generateApplyOfferWatcher() {
    yield takeLatest(APPLY_OFFER, applyOfferSaga);
}

/**
 * R65 item 1: the accepted offer leaves the cart (ordered, or the customer is back in the Warenkorb) —
 * the parked shop goods return; an offer that came onto an empty cart leaves it empty.
 */
function* restoreParkedCartSaga(): SagaIterator {
    const parked: ParkedCartState = yield select(getParkedCart);
    if (!parked.offerPublicId) return;

    if (!parked.cart) {
        yield put(clearParkedCart());
        yield put(resetShoppingCart());
        return;
    }

    yield put(requestOrderingCalc());

    const shopGoods = new ShoppingCart(parked.cart);
    const { error, response } = yield call(orderingCalc, CartRequestDTO.fromShoppingCart(shopGoods));

    if (error) yield put(failureOrderingCalc(error));
    else {
        yield put(clearParkedCart());
        yield put(successOrderingCalc(response, shopGoods.containsOrderDirectlyProduct));
    }
}

export function* generateRestoreParkedCartWatcher() {
    yield takeLatest(RESTORE_PARKED_CART, restoreParkedCartSaga);
}

/**
 * Fetch my offers
 */
function* fetchMyOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestMyOffers());

    const { error, response } = yield call(fetchMyOffers);

    if (error) {
        yield put(requestMyOffersFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: apiErrorMessage({ error }, i18next.t('translations:r65_cart_offer.load_error')),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else yield put(requestMyOffersSuccess(response));
}

export function* generateFetchMyOffersWatcher() {
    yield takeLatest(FETCH_MY_OFFERS, fetchMyOffersSaga);
}

/**
 * Clear My Offers
 */
function* clearMyOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestMyOffersSuccess({ response: { data: [] } }));
}

export function* generateClearMyOffersWatcher() {
    yield takeLatest(CLEAR_MY_OFFERS, clearMyOffersSaga);
}

/**
 * Fetch offers
 */
function* fetchOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestOffers());

    const { error, response } = yield call(fetchOffers);

    if (error) yield put(requestOffersFailure(error));
    else yield put(requestOffersSuccess(response));
}

export function* generateFetchOffersWatcher() {
    yield takeLatest(FETCH_OFFERS, fetchOffersSaga);
}
